import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaBolt, FaPlay, FaCheckCircle, FaTimesCircle, FaSpinner, FaFileInvoiceDollar, FaUsers, FaEllipsisH, FaPaperclip, FaFolderOpen } from 'react-icons/fa';
import accionesRapidasService from '../services/accionesRapidasService';
import '../styles/pages/AccionesRapidasPage.css';

const MESES = [
  { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' },
];

const ANIO_ACTUAL = new Date().getFullYear();
const ANIOS = [ANIO_ACTUAL - 2, ANIO_ACTUAL - 1, ANIO_ACTUAL, ANIO_ACTUAL + 1];

/**
 * Acciones Rápidas
 * Cada botón ejecuta una acción específica contra la API backend.
 * Las mismas APIs son consumibles externamente con Service Token (MCP).
 */
const AccionesRapidasPage = ({ token, id_company }) => {
  const navigate = useNavigate();

  return (
    <div className="ar-page">
      <header className="ar-header">
        <button className="ar-back-btn" onClick={() => navigate('/dashboard')}>
          <FaArrowLeft /> Volver
        </button>
        <div className="ar-header-info">
          <h1 className="ar-title"><FaBolt /> Acciones Rápidas</h1>
          <p className="ar-subtitle">Ejecuta acciones con un clic · APIs disponibles para integración externa</p>
        </div>
      </header>

      <div className="ar-sections">
        <AccionSeccion
          titulo="Impuestos IVA"
          icono={<FaFileInvoiceDollar />}
          descripcion="Consultas y acciones relacionadas con la declaración y estado del IVA"
          conPeriodo
        >
          {({ anual, mes }) => (<>
            <AccionBoton
              label="Balance"
              descripcion="Ejecuta automatización IVA, genera balance y entrega PDF"
              onExecute={async () => {
                const res = await accionesRapidasService.balanceIva(id_company, anual, mes, token);
                if (res.success && res.url) window.open(res.url, '_blank');
                return res;
              }}
            />
            <AccionBoton
              label="¿Cuánto IVA debo pagar?"
              descripcion="Calcula el monto de IVA a pagar en el período y genera el link de pago"
              onExecute={async () => {
                const res = await accionesRapidasService.linkPagoIva(id_company, anual, mes, token);
                if (res.success && res.url) window.open(res.url, '_blank');
                return res;
              }}
            />
            <AccionBoton
              label="¿El IVA fue pagado?"
              descripcion="Verifica si el IVA del período fue pagado y muestra el comprobante"
              onExecute={async () => {
                const res = await accionesRapidasService.estadoPagoIva(id_company, anual, mes, token);
                if (res.success && res.url) window.open(res.url, '_blank');
                return res;
              }}
            />
          </>)}
        </AccionSeccion>

        <AccionSeccion
          titulo="Recursos Humanos — Remuneraciones"
          icono={<FaUsers />}
          descripcion="Liquidaciones, cotizaciones y pagos previsionales del período"
          conPeriodo
        >
          {({ anual, mes }) => (<>
            <AccionBoton
              label="Liquidación de sueldo"
              descripcion="Genera las liquidaciones de todos los colaboradores activos del período"
              onExecute={async () => {
                const res = await accionesRapidasService.liquidacionesPeriodo(id_company, anual, mes, token);
                if (res.success && res.data) {
                  res.data.filter(r => r.pdf_url).forEach(r => window.open(r.pdf_url, '_blank'));
                }
                return res;
              }}
            />
            <AccionBoton
              label="Link de pago Previred"
              descripcion="Calcula el monto total de cotizaciones y genera link para que el cliente transfiera y adjunte comprobante"
              onExecute={async () => {
                const res = await accionesRapidasService.linkPagoPrevired(id_company, anual, mes, token);
                if (res.success && res.url) window.open(res.url, '_blank');
                return res;
              }}
            />
            <AccionBoton
              label="¿Se pagó Previred?"
              descripcion="Verifica si el certificado de pago Previred del período fue cargado"
              onExecute={async () => {
                const res = await accionesRapidasService.estadoPrevired(id_company, anual, mes, token);
                if (res.success && res.url) window.open(res.url, '_blank');
                return res;
              }}
            />
          </>)}
        </AccionSeccion>

        <AccionSeccion
          titulo="Recursos Humanos — Contratos y Gestión"
          icono={<FaUsers />}
          descripcion="Contratos, finiquitos y licencias médicas"
          conRut
        >
          {({ rut }) => (<>
            <AccionBoton
              label="Contrato de trabajo"
              descripcion="Obtiene el contrato de trabajo de un colaborador"
              onExecute={async () => {
                const res = await accionesRapidasService.contratoDeTrabajoByRut(id_company, rut, token);
                if (res.success && res.urls?.length) res.urls.forEach(u => window.open(u, '_blank'));
                return res;
              }}
            />
            <AccionBoton
              label="Obtener finiquito"
              descripcion="Obtiene el documento de finiquito de un colaborador"
              onExecute={async () => {
                const res = await accionesRapidasService.obtenerFiniquito(id_company, rut, token);
                if (res.success && res.urls?.length) res.urls.forEach(u => window.open(u, '_blank'));
                return res;
              }}
            />
          </>)}
        </AccionSeccion>


        <AccionSeccion
          titulo="Archivos Adjuntos"
          icono={<FaPaperclip />}
          descripcion="Carga de archivos al sistema — el banco y período se detectan automáticamente desde el nombre del archivo"
        >
          <AccionBotonArchivo
            label="Cargar cartola bancaria"
            descripcion='Sube el Excel de cartola. Nombra el archivo con el banco y período: "BCI_Enero_2025.xlsx", "BancoEstado_Marzo_2025.xlsx", etc.'
            onExecute={(file) => accionesRapidasService.cargarCartola(file, id_company, token)}
          />
        </AccionSeccion>

        <AccionSeccion
          titulo="Otras Peticiones"
          icono={<FaEllipsisH />}
          descripcion="Solicita al contador que se contacte contigo para gestionar estas acciones"
        >
          <AccionBoton
            label="Crear finiquito"
            descripcion="Solicita al contador que genere el finiquito de un colaborador"
            onExecute={() => accionesRapidasService.crearPeticion(id_company, 'Crear finiquito', token)}
          />
          <AccionBoton
            label="Registrar ingreso de nuevo empleado"
            descripcion="Solicita al contador que registre el ingreso de un nuevo colaborador"
            onExecute={() => accionesRapidasService.crearPeticion(id_company, 'Registrar ingreso de nuevo empleado', token)}
          />
          <AccionBoton
            label="Trámite en el SII"
            descripcion="Solicita al contador que gestione un trámite ante el SII"
            onExecute={() => accionesRapidasService.crearPeticion(id_company, 'Trámite en el SII', token)}
          />
          <AccionBoton
            label="Trámite de patente"
            descripcion="Solicita al contador que gestione un trámite de patente municipal"
            onExecute={() => accionesRapidasService.crearPeticion(id_company, 'Trámite de patente', token)}
          />
          <AccionBoton
            label="Rectificaciones"
            descripcion="Solicita al contador que realice una rectificación de declaración o documento"
            onExecute={() => accionesRapidasService.crearPeticion(id_company, 'Rectificaciones', token)}
          />
          <AccionBoton
            label="Observaciones SII"
            descripcion="Solicita al contador que gestione observaciones emitidas por el SII"
            onExecute={() => accionesRapidasService.crearPeticion(id_company, 'Observaciones SII', token)}
          />
        </AccionSeccion>
      </div>
    </div>
  );
};

/**
 * Subsección de acciones.
 *
 * Cuando conPeriodo=true, renderiza selectores de año y mes, y pasa
 * { anual, mes } a los hijos via render prop:
 *   <AccionSeccion conPeriodo ...>
 *     {({ anual, mes }) => <AccionBoton onExecute={() => service.fn(id_company, anual, mes, token)} />}
 *   </AccionSeccion>
 *
 * Sin conPeriodo, los hijos son JSX normal.
 */
export const AccionSeccion = ({ titulo, icono, descripcion, conPeriodo, conRut, children }) => {
  const [anual, setAnual] = useState(ANIO_ACTUAL);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [rut, setRut] = useState('');

  const contenido = typeof children === 'function'
    ? children({ anual, mes, rut })
    : children;

  const hasChildren = contenido != null && React.Children.count(contenido) > 0;

  return (
    <section className="ar-section">
      <div className="ar-section-header">
        <div className="ar-section-header-top">
          <h2 className="ar-section-title">
            {icono && <span className="ar-section-icon">{icono}</span>}
            {titulo}
          </h2>
          <div className="ar-periodo">
            {conRut && (
              <input
                className="ar-select"
                type="text"
                placeholder="RUT empleado"
                value={rut}
                onChange={e => setRut(e.target.value)}
              />
            )}
            {conPeriodo && (<>
              <select
                className="ar-select"
                value={mes}
                onChange={e => setMes(Number(e.target.value))}
              >
                {MESES.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                className="ar-select"
                value={anual}
                onChange={e => setAnual(Number(e.target.value))}
              >
                {ANIOS.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </>)}
          </div>
        </div>
        {descripcion && <p className="ar-section-desc">{descripcion}</p>}
      </div>
      {hasChildren
        ? <div className="ar-actions-grid">{contenido}</div>
        : <p className="ar-section-empty">Próximamente</p>
      }
    </section>
  );
};

/**
 * Botón de acción individual.
 *
 * @param {string} label - Texto del botón
 * @param {string} descripcion - Descripción corta de qué hace
 * @param {function} onExecute - Función async; debe retornar { success, message, data? }
 * @param {ReactNode} icon - Ícono opcional
 */
export const AccionBoton = ({ label, descripcion, onExecute, icon }) => {
  const [estado, setEstado] = useState('idle'); // idle | loading | success | error
  const [resultado, setResultado] = useState(null);

  const handleClick = async () => {
    setEstado('loading');
    setResultado(null);
    try {
      const res = await onExecute();
      setEstado(res.success ? 'success' : 'error');
      setResultado(res);
    } catch (err) {
      setEstado('error');
      setResultado({ success: false, message: err.message || 'Error inesperado' });
    }
  };

  return (
    <div className={`ar-action-card ar-action-card--${estado}`}>
      <div className="ar-action-top">
        <div className="ar-action-info">
          <span className="ar-action-label">
            {icon && <span className="ar-action-icon">{icon}</span>}
            {label}
          </span>
          {descripcion && <span className="ar-action-desc">{descripcion}</span>}
        </div>
        <button
          className="ar-action-btn"
          onClick={handleClick}
          disabled={estado === 'loading'}
        >
          {estado === 'loading' ? <FaSpinner className="ar-spinner" /> : <FaPlay />}
          {estado === 'loading' ? 'Ejecutando...' : 'Ejecutar'}
        </button>
      </div>

      {resultado && (
        <div className={`ar-result ar-result--${resultado.success ? 'success' : 'error'}`}>
          {resultado.success ? <FaCheckCircle /> : <FaTimesCircle />}
          <span>{resultado.message}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Variante de AccionBoton para acciones que requieren subir un archivo.
 * Muestra un selector de archivo y un botón "Cargar" separados.
 *
 * @param {function} onExecute - Recibe el File seleccionado; debe retornar { success, message }
 */
export const AccionBotonArchivo = ({ label, descripcion, onExecute, accept = '.xlsx,.xls,.csv' }) => {
  const [estado, setEstado] = useState('idle');
  const [resultado, setResultado] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const inputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    setArchivo(file);
    setEstado('idle');
    setResultado(null);
  };

  const handleCargar = async () => {
    if (!archivo) return;
    setEstado('loading');
    setResultado(null);
    try {
      const res = await onExecute(archivo);
      setEstado(res.success ? 'success' : 'error');
      setResultado(res);
      if (res.success) {
        setArchivo(null);
        if (inputRef.current) inputRef.current.value = '';
      }
    } catch (err) {
      setEstado('error');
      setResultado({ success: false, message: err.message || 'Error inesperado' });
    }
  };

  return (
    <div className={`ar-action-card ar-action-card--${estado}`}>
      <div className="ar-action-top">
        <div className="ar-action-info">
          <span className="ar-action-label">
            <span className="ar-action-icon"><FaPaperclip /></span>
            {label}
          </span>
          {descripcion && <span className="ar-action-desc">{descripcion}</span>}
        </div>
        <div className="ar-action-file-group">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={estado === 'loading'}
          />
          <button
            className="ar-action-btn ar-action-btn--secondary"
            onClick={() => inputRef.current?.click()}
            disabled={estado === 'loading'}
            type="button"
            title="Seleccionar archivo Excel o CSV"
          >
            <FaFolderOpen />
            <span className="ar-action-file-name">
              {archivo ? archivo.name : 'Seleccionar archivo'}
            </span>
          </button>
          <button
            className="ar-action-btn"
            onClick={handleCargar}
            disabled={!archivo || estado === 'loading'}
            type="button"
          >
            {estado === 'loading' ? <FaSpinner className="ar-spinner" /> : <FaPlay />}
            {estado === 'loading' ? 'Procesando...' : 'Cargar'}
          </button>
        </div>
      </div>

      {resultado && (
        <div className={`ar-result ar-result--${resultado.success ? 'success' : 'error'}`}>
          {resultado.success ? <FaCheckCircle /> : <FaTimesCircle />}
          <span>{resultado.message}</span>
        </div>
      )}
    </div>
  );
};

export default AccionesRapidasPage;
